<template>
    <modal @close="$emit('close')" ref="modal">
        <template v-slot:title>TCP+ Job Codes — {{ integration.client.name }}</template>

        <slot>
            <div v-if="loading" class="text-center my-3">
                <i class="far fa-spinner fa-spin"></i> Loading job codes from TCP+...
            </div>

            <div v-if="errorMsg" class="alert alert-danger py-2">
                {{ errorMsg }}
            </div>

            <div v-if="!loading && !errorMsg && summary">
                <div class="form-row mb-2 align-items-center">
                    <div class="col">
                        <strong>{{ totalRowsShown }}</strong> of {{ summary.rows.length }} jobcodes
                        across {{ Object.keys(summary.by_store).length }} store(s)
                    </div>
                    <div class="col-auto">
                        <label class="mb-0">
                            <input type="checkbox" v-model="includeIgnored" />
                            Show ignored codes (06–13, unknown)
                        </label>
                    </div>
                </div>

                <div v-for="storeKey in sortedStoreKeys" :key="storeKey" class="mb-3">
                    <h5 class="mb-1">
                        <span v-if="storeKey">Store {{ storeKey }}</span>
                        <span v-else class="text-muted">(no store assigned)</span>
                        <small class="text-muted ms-2">— {{ visibleRowsForStore(storeKey).length }} code(s)</small>
                    </h5>
                    <table class="table table-sm table-bordered mb-0">
                        <thead>
                            <tr>
                                <th>jobCodeId</th>
                                <th>Role</th>
                                <th>Description</th>
                                <th class="text-center" title="Clockable">Clk</th>
                                <th class="text-center" title="Active">Act</th>
                                <th class="text-center" title="Leave Code">Lv</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in visibleRowsForStore(storeKey)" :key="row.job_code_id"
                                :class="{'text-muted': !row.is_primary_role}">
                                <td>{{ row.job_code_id }}</td>
                                <td>
                                    {{ row.position_label || row.position_code || '?' }}
                                    <span v-if="row.is_primary_role" class="badge bg-primary ms-1">primary</span>
                                </td>
                                <td>{{ row.description }}</td>
                                <td class="text-center">{{ row.clockable ? '✓' : '' }}</td>
                                <td class="text-center">{{ row.active ? '✓' : '' }}</td>
                                <td class="text-center">{{ row.leave_code ? '✓' : '' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h5 class="mt-3">Role counts (across all codes)</h5>
                <table class="table table-sm table-bordered" style="max-width: 400px;">
                    <thead>
                        <tr><th>Role</th><th class="text-end">Count</th></tr>
                    </thead>
                    <tbody>
                        <tr v-for="(count, label) in summary.role_counts" :key="label">
                            <td>{{ label }}</td>
                            <td class="text-end">{{ count }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </slot>

        <template v-slot:footer>
            <div class="form-row">
                <div class="col">
                    <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Close</button>
                </div>
                <div class="col">
                    <button type="button" class="btn btn-block btn-primary" @click.prevent="fetchSummary()" :disabled="loading">Refresh</button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import api from '@/api'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    mixins: [ModalMixin],
    props: ['client', 'integration'],
    data() {
        return {
            loading: false,
            errorMsg: '',
            summary: null,
            includeIgnored: false,
        }
    },
    computed: {
        sortedStoreKeys() {
            if (!this.summary) return []
            return Object.keys(this.summary.by_store).sort((a, b) => {
                if (!a) return 1
                if (!b) return -1
                return a.localeCompare(b)
            })
        },
        totalRowsShown() {
            if (!this.summary) return 0
            return this.summary.rows.filter(r => this.includeIgnored || r.is_primary_role).length
        },
    },
    methods: {
        reset() {
            this.summary = null
            this.errorMsg = ''
            this.fetchSummary()
        },
        visibleRowsForStore(storeKey) {
            const rows = (this.summary?.by_store?.[storeKey] || [])
            return this.includeIgnored ? rows : rows.filter(r => r.is_primary_role)
        },
        fetchSummary() {
            this.loading = true
            this.errorMsg = ''
            const url = `/admin/posintegrations/${this.integration.id}/tcpplus/jobcodes`
            api.get(url).then(resp => {
                this.summary = resp
                this.loading = false
            }).catch(errors => {
                this.errorMsg = (errors.__all__ && errors.__all__[0]) || 'Failed to load job codes.'
                this.loading = false
            })
        },
    },
}
</script>
